import React, { useState } from 'react';
import PropTypes from 'prop-types';

import SwiperCore, { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import ConcertCard from './ConcertCard';
import Button from '../../../react/components/Button';

import { concertsArrayPropTypes } from '../propTypes';
import { getClassNames, isMediaMatched } from '../../../utils';

SwiperCore.use([Navigation]);

const ConcertsGroupedByMonth = ({ concerts, setModal }: ConcertsGroupedByMonthProps) => {
  const mediaQuery = isMediaMatched(1200);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthsPerLoad = 2;

  const [monthsToShow, setMonthsToShow] = useState(monthsPerLoad);

  if (concerts.length === 0) {
    return (
      <p>No concerts currently available</p>
    );
  }

  const getConcertsInAMonth = (month: number) => (
    concerts.filter(({ date }) => new Date(parseInt(date, 10) * 1000).getMonth() === month)
  );

  const isCurrentOffSaleDateTime = (finalTime: number) => (
    finalTime ? finalTime > Math.floor(Date.now() / 1000) : true
  );

  const months = Array.from(
    new Set(concerts.map((concert) => (
      new Date(parseInt(concert.date, 10) * 1000).getMonth()
    ))),
  );

  const numberOfConcertsByMonth = months.map((month) => ({
    month,
    concertsCount: getConcertsInAMonth(month)
      .filter(({ finalPerformanceOffSaleDateAndTime }) => isCurrentOffSaleDateTime(
        parseInt(finalPerformanceOffSaleDateAndTime, 10),
      ))
      .length,
  }));

  return (
    <>
      {months
        .filter((monthsMonth) => numberOfConcertsByMonth.find(({ month: countMonth }) => monthsMonth === countMonth)?.concertsCount !== 0) // eslint-disable-line max-len
        .map((month, index) => (
          <div
            key={month}
            className={getClassNames(null, 'c-cyop__month-wrapper c-card-carousel', {
              'c-cyop__month-wrapper--first': index === 0,
              'u-zero-height': index >= monthsToShow,
            })}
          >
            <p className="c-cyop__month-title">{monthNames[month]}</p>

            <Swiper
              className="c-card-carousel__container"
              direction="horizontal"
              slidesPerView={mediaQuery ? 3.5 : 2.2}
              spaceBetween={10}
              pagination={{
                el: '.swiper-pagination',
                clickable: true,
              }}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
            >
              {getConcertsInAMonth(month)
                .filter(({ finalPerformanceOffSaleDateAndTime }) => isCurrentOffSaleDateTime(
                  parseInt(finalPerformanceOffSaleDateAndTime, 10),
                ))
                .map((concert) => (
                  <SwiperSlide className="c-card-carousel__slide c-card-carousel__slide--cyop" key={`${month}-${concert.title}`}>
                    <ConcertCard
                      blocks={concert.detailedContent.blocks}
                      image={concert.image || ''}
                      performancesViewTeaser={concert.performancesViewTeaser}
                      productionSeasonId={concert.productionSeasonId}
                      setModal={setModal}
                      synopsis={concert.synopsis}
                      title={concert.title}
                    />
                  </SwiperSlide>
                ))}

              <div className="c-card-carousel__controls">
                <div className="swiper-pagination c-card-carousel__pagination" />

                <div className="swiper-button-prev c-card-carousel__button c-card-carousel__button--prev" />
                <div className="swiper-button-next c-card-carousel__button c-card-carousel__button--next" />
              </div>
            </Swiper>
          </div>
        ))}

      {months.length > monthsToShow && (
        <div className="c-cyop__button">
          <Button onClick={() => setMonthsToShow(monthsToShow + monthsPerLoad)}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
};

const concertsGroupedByMonthPropTypes = {
  concerts: concertsArrayPropTypes.isRequired,
  setModal: PropTypes.func.isRequired,
};

ConcertsGroupedByMonth.propTypes = concertsGroupedByMonthPropTypes;

type ConcertsGroupedByMonthProps = PropTypes.InferProps<typeof concertsGroupedByMonthPropTypes>;

export default ConcertsGroupedByMonth;
